import React, { useState } from "react";
import OTPModal from "../components/OTPModal";
import { Link, useNavigate } from "react-router-dom";
import "./StudentRegistration.css";
import mkcgLogo from "../assests/logo/logo.png";
import Swal from "sweetalert2";

const API_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5001/api";

/* =========================================================
   REGEX
========================================================= */

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const mobileRe = /^[6-9]\d{9}$/;

/* =========================================================
   PASSWORD STRENGTH
========================================================= */

function passwordStrength(pw) {
  if (!pw) {
    return {
      score: 0,
      label: "",
      color: "",
    };
  }

  let score = 0;

  if (pw.length >= 6 && pw.length <= 8) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[a-z]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;

  const map = [
    {
      label: "Too short",
      color: "#C0392B",
    },
    {
      label: "Weak",
      color: "#C0392B",
    },
    {
      label: "Fair",
      color: "#F2760C",
    },
    {
      label: "Good",
      color: "#1A6FB8",
    },
    {
      label: "Strong",
      color: "#3E9563",
    },
  ];

  return {
    score,
    ...map[score],
  };
}

/* =========================================================
   EYE ICON
========================================================= */

const EyeIcon = ({ open }) =>
  open ? (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  ) : (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );

/* =========================================================
   MAIN COMPONENT
========================================================= */

const StudentRegistration = () => {
  const navigate = useNavigate();

  /* -------------------------------------------------------
     FORM DATA
  ------------------------------------------------------- */

  const [formData, setFormData] = useState({
    name: "",
    dob: "",
    gender: "",
    mobile: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  /* -------------------------------------------------------
     STATES
  ------------------------------------------------------- */

  const [errors, setErrors] = useState({});

  const [showOtp, setShowOtp] = useState(false);

  const [emailVerified, setEmailVerified] = useState(false);

  const [showPw, setShowPw] = useState(false);

  const [showCpw, setShowCpw] = useState(false);

  const [submitted, setSubmitted] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const strength = passwordStrength(formData.password);

  /* =======================================================
     VALIDATION
  ======================================================= */

  const validate = (data = formData) => {
    const e = {};

    /* NAME */

    if (!data.name.trim()) {
      e.name = "Full name is required.";
    } else if (data.name.trim().length < 3) {
      e.name = "At least 3 characters required.";
    }

    /* DOB */

    if (!data.dob) {
      e.dob = "Date of birth is required.";
    } else {
      const dobDate = new Date(data.dob);

      const age = Math.floor(
        (Date.now() - dobDate.getTime()) /
          (1000 * 60 * 60 * 24 * 365.25)
      );

      if (age < 15) {
        e.dob = "Must be at least 15 years old.";
      }

      if (age > 60) {
        e.dob = "Enter a valid date of birth.";
      }
    }

    /* GENDER */

    if (!data.gender) {
      e.gender = "Please select a gender.";
    }

    /* MOBILE */

    if (!data.mobile) {
      e.mobile = "Mobile number is required.";
    } else if (!mobileRe.test(data.mobile)) {
      e.mobile = "Enter a valid 10-digit number.";
    }

    /* EMAIL */

    if (!data.email) {
      e.email = "Email address is required.";
    } else if (!emailRe.test(data.email)) {
      e.email = "Enter a valid email address.";
    } else if (!emailVerified) {
      e.email = "Verify your email via OTP first.";
    }

    /* PASSWORD */

    if (!data.password) {
      e.password = "Password is required.";
    } else if (
      data.password.length < 6 ||
      data.password.length > 8
    ) {
      e.password =
        "6-8 characters with Uppercase, Lowercase & Special character.";
    } else if (!/[A-Z]/.test(data.password)) {
      e.password =
        "Password must contain at least one uppercase letter.";
    } else if (!/[a-z]/.test(data.password)) {
      e.password =
        "Password must contain at least one lowercase letter.";
    } else if (!/[^A-Za-z0-9]/.test(data.password)) {
      e.password =
        "Password must contain at least one special character.";
    }

    /* CONFIRM PASSWORD */

    if (!data.confirmPassword) {
      e.confirmPassword = "Please confirm your password.";
    } else if (data.password !== data.confirmPassword) {
      e.confirmPassword = "Passwords do not match.";
    }

    return e;
  };

  /* =======================================================
     HANDLE INPUT CHANGE
  ======================================================= */

  const handleChange = (e) => {
    const { name, value } = e.target;

    let sanitized = value;

    /* Mobile only numbers */

    if (name === "mobile") {
      sanitized = value.replace(/\D/g, "").slice(0, 10);
    }

    /* Email modification means OTP must be verified again */

    if (name === "email") {
      setEmailVerified(false);
    }

    const updated = {
      ...formData,
      [name]: sanitized,
    };

    setFormData(updated);

    /* Validate field if it already has an error */

    if (errors[name]) {
      const errs = validate(updated);

      setErrors((prev) => ({
        ...prev,
        [name]: errs[name] || undefined,
      }));
    }
  };

  /* =======================================================
     GET OTP
  ======================================================= */

  const handleGetOtp = async () => {
    /* Name is needed so the OTP mail can greet the student */

    if (!formData.name.trim()) {
      setErrors((prev) => ({
        ...prev,
        name: "Enter your full name first.",
      }));

      return;
    }

    if (!formData.email) {
      setErrors((prev) => ({
        ...prev,
        email: "Enter your email first.",
      }));

      return;
    }

    if (!emailRe.test(formData.email)) {
      setErrors((prev) => ({
        ...prev,
        email: "Enter a valid email address.",
      }));

      return;
    }

    /* Open modal */

    setShowOtp(true);

    try {
      const response = await fetch(
        `${API_URL}/registration/send-otp`,
        {
          method: "POST",

          headers: {
            "Content-Type": "application/json",
          },

          body: JSON.stringify({
            email: formData.email,
            name: formData.name.trim(),
          }),
        }
      );

      if (!response.ok) {
        console.log("OTP API returned an error.");
      }
    } catch (err) {
      console.log("OTP error:", err);
    }
  };

  /* =======================================================
     SUBMIT REGISTRATION
  ======================================================= */

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errs = validate();

    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/registration/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.success) {
        await Swal.fire({
          icon: "success",
          title: "Registration Successful",
        });

        setSubmitted(true);
      } else {
        Swal.fire({
          icon: "error",
          title: "Registration Failed",
          text: data.message || "Registration failed. Please try again.",
          confirmButtonColor: "#0d6efd",
        });
      }
    } catch (err) {
      console.log("Registration error:", err);

      Swal.fire({
        icon: "error",
        title: "Connection Error",
        text: "Unable to connect to the server. Please try again.",
        confirmButtonColor: "#0d6efd",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  /* =======================================================
     CURRENT DATE FOR DOB
  ======================================================= */

  const today = new Date()
    .toISOString()
    .split("T")[0];

  /* =======================================================
     FIELD HELPER
  ======================================================= */

  const fi = (name) => ({
    className: `sr-input${
      errors[name] ? " sr-input-error" : ""
    }`,

    name,

    value: formData[name],

    onChange: handleChange,
  });

  /* =======================================================
     SUCCESS SCREEN
  ======================================================= */

  if (submitted) {
    return (
      <div className="ig-page">

        {/* ───────────────── TOP NAVBAR ───────────────── */}
        <header className="ig-navbar">

          <div className="ig-navbar-left">

            <img
              className="logo-image"
              src={mkcgLogo}
              alt="MKCG Medical College"
            />

            <div className="ig-navbar-titles">

              <h1 className="ig-navbar-title">
                MKCG MEDICAL COLLEGE, BERHAMPUR, ODISHA, INDIA
              </h1>

              <p className="ig-navbar-subtitle">
                ମହାରାଜା କୃଷ୍ଣଚନ୍ଦ୍ର ଗଜପତି ଭେଷଜ ମହାବିଦ୍ୟାଳୟ, ବ୍ରହ୍ମପୁର, ଓଡ଼ିଶା, ଭାରତ
              </p>

            </div>
          </div>

          <div className="ig-navbar-right">

            <span className="ig-navbar-badge">
              STUDENT INTERFACE
            </span>

            <Link
              to="/"
              className="ig-home-btn"
            >
              Return to Home
            </Link>

          </div>

        </header>

        {/* SUCCESS BODY */}

        <div className="sr-wrap">

          <div className="sr-card">

            <aside className="sr-side">

              <LeftPanel />

            </aside>

            <section className="sr-main sr-success-main">

              <div className="sr-success-card">

                <div className="sr-success-ring">

                  <div className="sr-success-icon">
                    ✓
                  </div>

                </div>

                <h2>
                  Registration Step 1 Process Successful!
                </h2>

                <p>
                  Welcome,{" "}
                  <strong>
                    {formData.name}
                  </strong>
                  .
                  <br />
                  Your student account has been created successfully.
                </p>

                <button
                  type="button"
                  className="sr-submit-btn sr-success-btn"
                  onClick={() => navigate("/StudentLogin")}
                >
                  Proceed to Login →
                </button>

              </div>

            </section>

          </div>

        </div>

      </div>
    );
  }

  /* =======================================================
     MAIN PAGE
  ======================================================= */

  return (

    <div className="ig-page">

      {/* ───────────────── TOP NAVBAR ───────────────── */}
      <header className="ig-navbar">

        <div className="ig-navbar-left">

          <img
            className="logo-image"
            src={mkcgLogo}
            alt="MKCG Medical College"
          />

          <div className="ig-navbar-titles">

            <h1 className="ig-navbar-title">
              MKCG MEDICAL COLLEGE, BERHAMPUR, ODISHA, INDIA
            </h1>

            <p className="ig-navbar-subtitle">
              ମହାରାଜା କୃଷ୍ଣଚନ୍ଦ୍ର ଗଜପତି ଭେଷଜ ମହାବିଦ୍ୟାଳୟ, ବ୍ରହ୍ମପୁର, ଓଡ଼ିଶା, ଭାରତ
            </p>

          </div>
        </div>

        <div className="ig-navbar-right">

          <span className="ig-navbar-badge">
            STUDENT INTERFACE
          </span>

          <Link
            to="/"
            className="ig-home-btn"
          >
            Return to Home
          </Link>

        </div>

      </header>

      {/* ===================================================
          MAIN WRAPPER
      =================================================== */}

      <div className="sr-wrap">

        <div className="sr-card">

          {/* =================================================
              LEFT PANEL
          ================================================= */}

          <aside className="sr-side">

            <LeftPanel />

          </aside>

          {/* =================================================
              RIGHT PANEL
          ================================================= */}

          <section className="sr-main">

            <div className="sr-form-scroll">

              {/* TITLE */}

              <div className="sr-title-area">

                <h2 className="sr-form-title">
                  Basic Details of Student
                </h2>

              </div>

              {/* FORM */}

              <form
                onSubmit={handleSubmit}
                noValidate
              >

                {/* =========================================
                    NAME + DOB
                ========================================= */}

                <div className="sr-row">

                  <div className="sr-field">

                    <label>
                      Full Name{" "}
                      <span>*</span>
                    </label>

                    <input
                      {...fi("name")}
                      type="text"
                      maxLength={100}
                      placeholder="Enter your full name"
                      autoComplete="name"
                    />

                    {errors.name && (
                      <p className="sr-error">
                        ⚠ {errors.name}
                      </p>
                    )}

                  </div>

                  <div className="sr-field">

                    <label>
                      Date of Birth{" "}
                      <span>*</span>
                    </label>

                    <input
                      {...fi("dob")}
                      type="date"
                      max={today}
                    />

                    {errors.dob && (
                      <p className="sr-error">
                        ⚠ {errors.dob}
                      </p>
                    )}

                  </div>

                </div>

                {/* =========================================
                    GENDER + MOBILE
                ========================================= */}

                <div className="sr-row">

                  <div className="sr-field">

                    <label>
                      Gender{" "}
                      <span>*</span>
                    </label>

                    <select
                      className={`sr-input${
                        errors.gender
                          ? " sr-input-error"
                          : ""
                      }`}
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                    >

                      <option value="">
                        Select your gender
                      </option>

                      <option value="1">
                        Male
                      </option>

                      <option value="2">
                        Female
                      </option>

                      <option value="Other">
                        Other
                      </option>

                    </select>

                    {errors.gender && (
                      <p className="sr-error">
                        ⚠ {errors.gender}
                      </p>
                    )}

                  </div>

                  <div className="sr-field">

                    <label>
                      Mobile Number{" "}
                      <span>*</span>
                    </label>

                    <div
                      className={`sr-input-group${
                        errors.mobile
                          ? " sr-group-error"
                          : ""
                      }`}
                    >

                      <span className="sr-prefix">
                        +91
                      </span>

                      <input
                        type="tel"
                        inputMode="numeric"
                        name="mobile"
                        value={formData.mobile}
                        onChange={handleChange}
                        placeholder="10-digit mobile number"
                        maxLength={10}
                      />

                    </div>

                    {errors.mobile && (
                      <p className="sr-error">
                        ⚠ {errors.mobile}
                      </p>
                    )}

                  </div>

                </div>

                {/* =========================================
                    EMAIL
                ========================================= */}

                <div className="sr-field">

                  <label>
                    Email Address{" "}
                    <span>*</span>
                  </label>

                  <p className="sr-email-note">
                    (OTP will be forwarded to this email.
                    Subsequent communications will be through
                    this email only.)
                  </p>

                  <div
                    className={`sr-input-group sr-email-group${
                      errors.email
                        ? " sr-group-error"
                        : ""
                    }`}
                  >

                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter your email"
                      autoComplete="email"
                      maxLength={100}
                    />

                    <button
                      type="button"
                      className={`sr-otp-btn${
                        emailVerified
                          ? " sr-otp-verified"
                          : ""
                      }`}
                      onClick={handleGetOtp}
                      disabled={emailVerified}
                    >
                      {emailVerified
                        ? "✓ Verified"
                        : "Get OTP"}
                    </button>

                  </div>

                  {errors.email && (
                    <p className="sr-error">
                      ⚠ {errors.email}
                    </p>
                  )}

                </div>

                {/* =========================================
                    PASSWORD + CONFIRM PASSWORD
                ========================================= */}

                <div className="sr-row">

                  {/* PASSWORD */}

                  <div className="sr-field">

                    <label>
                      Password{" "}
                      <span>*</span>
                    </label>

                    <div
                      className={`sr-input-group${
                        errors.password
                          ? " sr-group-error"
                          : ""
                      }`}
                    >

                      <input
                        type={
                          showPw
                            ? "text"
                            : "password"
                        }
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="Create a password"
                        autoComplete="new-password"
                        maxLength={8}
                      />

                      <button
                        type="button"
                        className="sr-eye"
                        onClick={() =>
                          setShowPw((v) => !v)
                        }
                        tabIndex={-1}
                        aria-label={
                          showPw
                            ? "Hide password"
                            : "Show password"
                        }
                      >
                        <EyeIcon open={showPw} />
                      </button>

                    </div>

                    <p className="sr-password-note">
                      6-8 characters with Uppercase, Lowercase & Special character.
                    </p>

                    {/* PASSWORD STRENGTH */}

                    {formData.password && (
                      <div className="sr-strength">

                        <div className="sr-strength-bars">

                          {[1, 2, 3, 4].map(
                            (i) => (
                              <div
                                key={i}
                                className="sr-strength-segment"
                                style={{
                                  background:
                                    i <= strength.score
                                      ? strength.color
                                      : "#E5E9EF",
                                }}
                              />
                            )
                          )}

                        </div>

                        <span
                          className="sr-strength-label"
                          style={{
                            color: strength.color,
                          }}
                        >
                          {strength.label}
                        </span>

                      </div>
                    )}

                    {errors.password && (
                      <p className="sr-error">
                        ⚠ {errors.password}
                      </p>
                    )}

                  </div>

                  {/* CONFIRM PASSWORD */}

                  <div className="sr-field">

                    <label>
                      Confirm Password{" "}
                      <span>*</span>
                    </label>

                    <div
                      className={`sr-input-group${
                        errors.confirmPassword
                          ? " sr-group-error"
                          : ""
                      }`}
                    >

                      <input
                        type={
                          showCpw
                            ? "text"
                            : "password"
                        }
                        name="confirmPassword"
                        value={
                          formData.confirmPassword
                        }
                        onChange={handleChange}
                        placeholder="Confirm your password"
                        autoComplete="new-password"
                        maxLength={8}
                      />

                      <button
                        type="button"
                        className="sr-eye"
                        onClick={() =>
                          setShowCpw((v) => !v)
                        }
                        tabIndex={-1}
                        aria-label={
                          showCpw
                            ? "Hide password"
                            : "Show password"
                        }
                      >
                        <EyeIcon open={showCpw} />
                      </button>

                    </div>

                    {errors.confirmPassword && (
                      <p className="sr-error">
                        ⚠ {errors.confirmPassword}
                      </p>
                    )}

                  </div>

                </div>

                {/* =========================================
                    NOTE
                ========================================= */}

                <p className="sr-required-note">
                  <span>*</span> Fields marked with * are
                  mandatory.
                </p>

                {/* =========================================
                    SUBMIT
                ========================================= */}

                <button
                  type="submit"
                  className="sr-submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? "Creating Account..."
                    : "Create Account →"}
                </button>

              </form>

            </div>

          </section>

        </div>

      </div>

      {/* ===================================================
          OTP MODAL
      =================================================== */}

      <OTPModal
        show={showOtp}
        email={formData.email}
        name={formData.name.trim()}
        onClose={() => setShowOtp(false)}
        onVerified={() => {
          setEmailVerified(true);

          setErrors((prev) => ({
            ...prev,
            email: undefined,
          }));

          setShowOtp(false);
        }}
      />

    </div>
  );
};

/* =========================================================
   LEFT PANEL
========================================================= */

const LeftPanel = () => {
  return (
    <div className="sr-left-inner">

      <div>

        <img
          src={mkcgLogo}
          alt="MKCG Medical College"
        />

      </div>

      <h2 className="sr-college-name">
        M.K.C.G. MEDICAL COLLEGE
      </h2>

      <p className="sr-college-location">
        BERHAMPUR, ODISHA, INDIA
      </p>

      <p className="sr-college-odia">
        ମହାରାଜା କୃଷ୍ଣଚନ୍ଦ୍ର ଗଜପତି ଭେଷଜ ମହାବିଦ୍ୟାଳୟ
        <br />
        ବ୍ରହ୍ମପୁର, ଓଡ଼ିଶା, ଭାରତ
      </p>

      <div className="sr-left-divider" />

      <p className="sr-government">
        (GOVT. OF ODISHA)
      </p>

    </div>
  );
};

export default StudentRegistration;
